import React, { useEffect, useState } from "react";
import {
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Grid,
    Button,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Chip,
    Divider,
    Box,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import EmailIcon from "@mui/icons-material/Email";
import PaymentIcon from "@mui/icons-material/Payment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { Title, useDataProvider, useNotify } from "react-admin";
import { blue, green, red, orange, grey } from "@mui/material/colors";
import HtmlEditorDialog from "./HtmlEditorDialog"; // Importa il dialog per l'editing HTML

function TimezoneCard() {
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => clearInterval(interval); // Pulisce l'intervallo al momento dello smontaggio
    }, []);

    return (
        <Card sx={{ mt: 5, backgroundColor: "#e8f5e9", textAlign: "center" }}>
            <CardContent>
                <Typography variant="h5" sx={{ color: "#388e3c" }}>
                    Data e Ora Attuale
                </Typography>
                <Typography variant="h6" sx={{ mt: 2 }}>
                    {currentTime.toLocaleString()} ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                </Typography>
            </CardContent>
        </Card>
    );
}


function SettingsPage() {
    const [settings, setSettings] = useState(null); // Stato per i parametri SMTP
    const [templates, setTemplates] = useState([]); // Stato per i template
    const [stripeTemplates, setStripeTemplates] = useState([]); // Template Stripe
    const [loading, setLoading] = useState(true); // Stato di caricamento
    const [dialogOpen, setDialogOpen] = useState(false); // Stato per dialog generica
    const [htmlDialogOpen, setHtmlDialogOpen] = useState(false); // Stato per dialog HTML
    const [subjectDialogOpen, setSubjectDialogOpen] = useState(false); // Dialog per subject
    const [fieldToEdit, setFieldToEdit] = useState(null); // Campo corrente da modificare
    const [templateToEdit, setTemplateToEdit] = useState(null); // Template corrente per editing HTML
    const [fieldValue, setFieldValue] = useState(""); // Valore corrente del campo
    const dataProvider = useDataProvider();
    const notify = useNotify();

    useEffect(() => {
        const fetchSettingsAndTemplates = async () => {
            try {
                const [settingsResponse, templatesResponse] = await Promise.all([
                    dataProvider.getList("settings", { pagination: { page: 1, perPage: 1 } }),
                    dataProvider.getList("tipo-servizio-mail", {
                        pagination: { page: 1, perPage: 50 }
                    }),
                ]);
                setSettings(settingsResponse.data[0] || {});
                
                // Separa template Stripe da altri template
                const allTemplates = templatesResponse.data || [];
                const stripe = allTemplates.filter(t => t.nome?.startsWith('stripe_'));
                const others = allTemplates.filter(t => !t.nome?.startsWith('stripe_'));
                
                setStripeTemplates(stripe);
                setTemplates(others);
            } catch (error) {
                notify(`Errore nel caricamento dei dati: ${error.message}`, { type: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchSettingsAndTemplates();
    }, [dataProvider, notify]);

    const handleOpenDialog = (field, value) => {
        setFieldToEdit(field);
        setFieldValue(value);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setFieldToEdit(null);
        setFieldValue("");
    };

    const handleSaveField = async () => {
        try {
            const updatedSettings = { ...settings, [fieldToEdit]: fieldValue };
            await dataProvider.update("settings", {
                id: settings.id,
                data: { [fieldToEdit]: fieldValue }
            });
            setSettings(updatedSettings);
            notify(`${fieldToEdit} aggiornato con successo`, { type: "success" });
        } catch (error) {
            notify(`Errore durante l'aggiornamento: ${error.message}`, { type: "error" });
        } finally {
            handleCloseDialog();
        }
    };

    const handleOpenHtmlDialog = (template) => {
        setTemplateToEdit(template);
        setHtmlDialogOpen(true);
    };

    const handleCloseHtmlDialog = () => {
        setHtmlDialogOpen(false);
        setTemplateToEdit(null);
    };

    const handleSaveHtml = async (updatedHtml) => {
        try {
            // Aggiorna sia templates che stripeTemplates
            const updatedTemplates = templates.map((template) =>
                template.id === templateToEdit.id
                    ? { ...template, html: updatedHtml }
                    : template
            );
            const updatedStripeTemplates = stripeTemplates.map((template) =>
                template.id === templateToEdit.id
                    ? { ...template, html: updatedHtml }
                    : template
            );
            
            setTemplates(updatedTemplates);
            setStripeTemplates(updatedStripeTemplates);

            await dataProvider.update("tipo-servizio-mail", {
                id: templateToEdit.id,
                data: { html: updatedHtml }
            });

            notify(`Template aggiornato con successo`, { type: "success" });
        } catch (error) {
            notify(`Errore durante il salvataggio: ${error.message}`, { type: "error" });
        } finally {
            handleCloseHtmlDialog();
        }
    };

    const handleOpenSubjectDialog = (template) => {
        setTemplateToEdit(template);
        setFieldValue(template.subject);
        setSubjectDialogOpen(true);
    };

    const handleCloseSubjectDialog = () => {
        setSubjectDialogOpen(false);
        setTemplateToEdit(null);
        setFieldValue("");
    };

    const handleSaveSubject = async () => {
        try {
            const updatedStripeTemplates = stripeTemplates.map((template) =>
                template.id === templateToEdit.id
                    ? { ...template, subject: fieldValue }
                    : template
            );
            setStripeTemplates(updatedStripeTemplates);

            await dataProvider.update("tipo-servizio-mail", {
                id: templateToEdit.id,
                data: { subject: fieldValue }
            });

            notify(`Subject aggiornato con successo`, { type: "success" });
        } catch (error) {
            notify(`Errore durante l'aggiornamento: ${error.message}`, { type: "error" });
        } finally {
            handleCloseSubjectDialog();
        }
    };

    const handleTestEmail = async () => {
        try {
            await dataProvider.create('generatore-mail', {
                data: {
                    payload: {
                        to: settings.destinatarioNotifiche,
                        tipoServizioMailId: 4, // ID del template di test
                        html: {
                            /* BLOCCO DOVE DFINIRE I PLACEHOLDERS */
                            email: settings.destinatarioNotifiche,
                            azienda: settings.nomeAzienda,
                            hostname: settings.hostname
                        },
                    }
                }
            });
            notify('Email di test inviata con successo!', { type: 'success' });
        } catch (error) {
            notify(`Errore durante l'invio dell'email di test: ${error.message}`, { type: 'error' });
        }
    };

    if (loading) {
        return <CircularProgress />;
    }

    return (
        <Card sx={{ mt: 10, textAlign: "center", backgroundColor: "#f3f6f9" }}>
            <Title title="Impostazioni" />
            <CardContent>
                <SettingsIcon sx={{ color: blue[500] }} />
                <Typography variant="h4" sx={{ margin: "20px 0", color: "#01b5d0" }}>
                    Impostazioni Generali
                </Typography>

                {/* Sezione check Timezone */}
                <TimezoneCard />

                {/* Sezione SMTP */}
                <Typography variant="h5" sx={{ marginTop: "30px", color: "#333" }}>
                    Parametri SMTP
                </Typography>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    {["smtphost", "smtpusername", "smtppassword", "smtpport", "destinatarioNotifiche"].map(
                        (field) => (
                            <Grid item xs={12} md={6} key={field}>
                                <TextField
                                    label={field}
                                    value={settings[field] || ""}
                                    fullWidth
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                            <Button onClick={() => handleOpenDialog(field, settings[field])}>
                                                Modifica
                                            </Button>
                                        ),
                                    }}
                                    variant="outlined"
                                />
                            </Grid>
                        )
                    )}
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleTestEmail}
                            sx={{ mt: 3 }}
                        >
                            Test Email
                        </Button>
                    </Grid>
                </Grid>

                {/* Sezione Template Stripe */}
                <Divider sx={{ my: 5 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <PaymentIcon sx={{ fontSize: 40, color: blue[500] }} />
                    <Typography variant="h5" sx={{ color: "#333" }}>
                        Template Email Stripe (Pagamenti)
                    </Typography>
                </Box>
                
                {stripeTemplates.length === 0 && (
                    <Card sx={{ mb: 3, backgroundColor: orange[50], border: `2px solid ${orange[300]}` }}>
                        <CardContent>
                            <Typography variant="h6" color="warning">
                                ⚠️ Template Stripe Non Trovati
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Esegui lo script SQL per creare i template:
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace', backgroundColor: grey[200], p: 1, borderRadius: 1 }}>
                                mysql -u dev -p backend_besocial &lt; sql/insert_stripe_email_templates.sql
                            </Typography>
                        </CardContent>
                    </Card>
                )}

                <Grid container spacing={3} sx={{ mt: 2 }}>
                    {stripeTemplates.map((template) => {
                        // Determina icona e colore in base al tipo
                        let icon, color, statusLabel;
                        if (template.nome === 'stripe_payment_pending') {
                            icon = <HourglassEmptyIcon />;
                            color = grey[700];
                            statusLabel = 'Pending';
                        } else if (template.nome === 'stripe_payment_succeeded') {
                            icon = <CheckCircleIcon />;
                            color = green[600];
                            statusLabel = 'Succeeded';
                        } else if (template.nome === 'stripe_payment_failed') {
                            icon = <ErrorIcon />;
                            color = red[600];
                            statusLabel = 'Failed';
                        } else if (template.nome === 'stripe_payment_canceled') {
                            icon = <CancelIcon />;
                            color = orange[600];
                            statusLabel = 'Canceled';
                        }

                        return (
                            <Grid item xs={12} md={6} key={template.id}>
                                <Card sx={{ 
                                    mb: 2, 
                                    border: `2px solid ${color}`,
                                    '&:hover': { boxShadow: 6 }
                                }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Box sx={{ color }}>{icon}</Box>
                                            <Typography variant="h6" sx={{ color }}>
                                                {statusLabel}
                                            </Typography>
                                            <Chip 
                                                label={template.nome} 
                                                size="small" 
                                                sx={{ ml: 'auto', fontFamily: 'monospace', fontSize: '0.7rem' }}
                                            />
                                        </Box>
                                        
                                        <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                                            <strong>Subject:</strong>
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <TextField
                                                value={template.subject}
                                                fullWidth
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                                variant="outlined"
                                            />
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => handleOpenSubjectDialog(template)}
                                            >
                                                Modifica
                                            </Button>
                                        </Box>

                                        <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                                            <strong>Placeholder Disponibili:</strong>
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {['userName', 'amount', 'description', 'transactionId', 'paymentIntentId', 'date', 'retryUrl'].map(placeholder => (
                                                <Chip 
                                                    key={placeholder}
                                                    label={`{{${placeholder}}}`}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                                                />
                                            ))}
                                        </Box>

                                        <Button
                                            variant="contained"
                                            fullWidth
                                            startIcon={<EmailIcon />}
                                            onClick={() => handleOpenHtmlDialog(template)}
                                            sx={{ mt: 3, backgroundColor: color, '&:hover': { backgroundColor: color, opacity: 0.9 } }}
                                        >
                                            Modifica Template HTML
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Sezione Templates Generici */}
                <Divider sx={{ my: 5 }} />
                <Typography variant="h5" sx={{ marginTop: "40px", color: "#333" }}>
                    Altri Templates Email
                </Typography>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    {templates.map((template) => (
                        <Grid item xs={12} md={6} key={template.id}>
                            <Card sx={{ mb: 2 }}>
                                <CardContent>
                                    <Typography variant="h6">{template.nome}</Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        <strong>Subject:</strong> {template.subject}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        <strong>HTML:</strong>
                                        <Button
                                            onClick={() => handleOpenHtmlDialog(template)}
                                            sx={{ ml: 2 }}
                                        >
                                            Modifica HTML
                                        </Button>
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </CardContent>

            {/* Dialog per modifica parametri SMTP */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog}>
                <DialogTitle>Modifica {fieldToEdit}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label={fieldToEdit}
                        value={fieldValue}
                        onChange={(e) => setFieldValue(e.target.value)}
                        variant="outlined"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="secondary">
                        Annulla
                    </Button>
                    <Button onClick={handleSaveField} color="primary">
                        Salva
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog per modifica Subject */}
            <Dialog open={subjectDialogOpen} onClose={handleCloseSubjectDialog} maxWidth="md" fullWidth>
                <DialogTitle>Modifica Subject Email</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Subject"
                        value={fieldValue}
                        onChange={(e) => setFieldValue(e.target.value)}
                        variant="outlined"
                        sx={{ mt: 2 }}
                        helperText="Puoi usare placeholder come {{userName}}, {{amount}}, ecc."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseSubjectDialog} color="secondary">
                        Annulla
                    </Button>
                    <Button onClick={handleSaveSubject} color="primary" variant="contained">
                        Salva
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog per modifica HTML */}
            {templateToEdit && (
                <HtmlEditorDialog
                    open={htmlDialogOpen}
                    onClose={handleCloseHtmlDialog}
                    onSave={handleSaveHtml}
                    initialHtml={templateToEdit.html}
                    placeholders={
                        templateToEdit.nome?.startsWith('stripe_') 
                        ? ["userName", "amount", "description", "transactionId", "paymentIntentId", "date", "retryUrl"]
                        : ["nome", "cognome", "email", "link", "azienda", "resetLink", "linkPrivacy", "hostname"]
                    }
                />
            )}
        </Card>
    );
}

export default SettingsPage;
